import type { FetchPolicy } from '@apollo/client';
import { useQuery } from '@apollo/client';
import type { SetNonNullable, SetRequired } from 'type-fest';
import { useMemo } from 'react';
import { gql } from '../graphql/generated/gql';
import type { AllTopicsWithPostsQuery } from '../graphql/generated/graphql';

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
const ALL_TOPIC_POSTS_QUERY = gql(`
  # eslint-disable-next-line slab-graphql/no-unused-fields
  query allTopicsWithPosts {
    currentSession {
      organization {
        id
        topics {
          id
          ... on Topic {
            allPosts(orderBy: TITLE, type: PUBLISHED) {
              edges {
                node {
                  id
                  publishedAt
                  archivedAt
                  type
                  ...PostTitleWithIcons_post
                }
                pinnedOrder
              }
            }
          }
        }
      }
    }
  }
`);

type TopicsPostsEdges = NonNullable<
  Extract<
    AllTopicsWithPostsQuery['currentSession']['organization']['topics'][number],
    { __typename?: 'Topic' }
  >['allPosts']
>['edges'];

type NullableTopicsPostsEdge = NonNullable<TopicsPostsEdges>[number];

export type TopicsPostsEdge = SetRequired<
  SetNonNullable<NonNullable<NullableTopicsPostsEdge>, 'node'>,
  'node'
>;

function ensureNonNullableNodes(
  edges: NullableTopicsPostsEdge[],
): TopicsPostsEdge[] {
  return edges.filter((edge): edge is TopicsPostsEdge => !!edge?.node);
}

const getAllTopicPosts = (data: AllTopicsWithPostsQuery | undefined) => {
  if (!data) {
    return null;
  }

  const { topics } = data.currentSession.organization;

  const map: Record<string, TopicsPostsEdge[]> = {};
  topics.forEach((topic) => {
    if (topic.__typename === 'Topic' && topic.allPosts?.edges) {
      map[topic.id] = ensureNonNullableNodes(topic.allPosts.edges);
    }
  });

  return map;
};

const useAllTopicPostsFull = (options: { fetchPolicy?: FetchPolicy }) => {
  const response = useQuery(ALL_TOPIC_POSTS_QUERY, {
    errorPolicy: 'ignore',
    ...options,
  });

  return useMemo(
    () => ({
      ...response,
      data: getAllTopicPosts(response.data),
    }),
    [response],
  );
};

export default useAllTopicPostsFull;
